import type React from "react"

import { useState, useEffect } from "react"
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, ExternalLink, Users, Calendar } from "lucide-react"
import { Link } from "react-router-dom"
import { getRiskFactorDeals } from "api/helpers";
import moment from "moment"
import { CheckboxInput, CheckboxLabel, StrikeThroughText } from "components/DealPage/DealPage.styled"
import CheckWithCircle from "static/svgs/CheckWithCircle"
import styled from "styled-components"

// Custom styled components for lighter checkboxes
const LightCheckboxLabel = styled(CheckboxLabel)`
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  background: linear-gradient(120deg, #e5e7eb 0%, #f3f4f6 100%) !important;
  border: 1px solid #d1d5db !important;
  margin-right: 8px !important;
  margin-top: 2px !important;
  
  svg {
    display: none;
  }
`

const LightCheckboxInput = styled(CheckboxInput)`
  &:checked + ${LightCheckboxLabel} {
    background: none !important;
    transform: none !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    margin-top: 0px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    svg {
      display: block;
      width: 16px !important;
      height: 16px !important;
      margin-top: 0px !important;
    }
  }
`

const NextStepContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  margin-bottom: 8px;
`

const NextStepText = styled.div`
  flex: 1;
  line-height: 1.4;
  margin-top: 2px;
`

interface RiskFactor {
  theme: string
  severity: "high" | "medium" | "low" | "positive"
  title: string
  description: string
  score?: number
}

interface ActionItem {
  assignee: string
  item: string
}

interface PivotalDeal {
  id: string
  company: string
  amount?: number
  salesRep?: string
  stage: string
  closeDate?: string
  probability?: number
  riskScore: number
  dealType?: "New Business" | "Upsell" | "Down sell" | "Flat Renewal"
  riskFactors: RiskFactor[]
  nextSteps?: string[]
  callSummary?: string
  callSummaryCreatedAt?: string
}

interface ApiRiskFactorDeal {
  deal_id: string
  deal_info: {
    deal_name: string
    deal_stage: string
    opportunity?: {
      close_date?: string
      amount?: number
    }
    user_id?: string
  }
  score: number
  top_risks: Array<{
    category: string
    reason: string
    risk_priority: "high" | "medium" | "low"
    title: string
    score?: number
    theme_title?: string
  }>
  call_summary?: {
    action_items?: ActionItem[]
    call_summary?: string
    created_at?: string
  } 
}

// Custom Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white rounded-lg text-black border border-gray-200 shadow-sm ${className}`}>{children}</div>
)

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
)

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`px-6 pb-4 ${className}`}>{children}</div>
)

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
)

const CardDescription = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-sm text-gray-600 ${className}`}>{children}</p>
)

const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode
  variant?: "default" | "outline"
  className?: string
}) => {
  const baseClasses = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
  const variantClasses =
    variant === "outline"
      ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
      : "bg-gray-100 text-gray-800"

  return <span className={`${baseClasses} ${variantClasses} ${className}`}>{children}</span>
}

const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
}: {
  children: React.ReactNode
  variant?: "default" | "ghost"
  size?: "default" | "sm"
  className?: string
  onClick?: () => void
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background"
  const variantClasses =
    variant === "ghost" ? "hover:bg-gray-100 hover:text-gray-900" : "bg-blue-600 text-white hover:bg-blue-700"
  const sizeClasses = size === "sm" ? "h-9 px-3 text-sm" : "h-10 py-2 px-4"

  return (
    <button className={`${baseClasses} ${variantClasses} ${sizeClasses} ${className}`} onClick={onClick}>
      {children}
    </button>
  )
}

// Sort risk factors by priority first, then by score
const sortRiskFactors = (riskFactors: RiskFactor[]): RiskFactor[] => {
  const severityOrder: Record<string, number> = { high: 3, medium: 2, low: 1, positive: 0 }
  
  return [...riskFactors].sort((a, b) => {
    // First sort by severity/priority (high to low)
    const aSeverity = severityOrder[a.severity] || 0
    const bSeverity = severityOrder[b.severity] || 0
    const severityDiff = bSeverity - aSeverity
    
    if (severityDiff !== 0) {
      return severityDiff
    }
    
    // If severity is the same, sort by score (higher score first)
    const scoreA = a.score || 0
    const scoreB = b.score || 0
    return scoreB - scoreA
  })
}

// Transform API data to component format
const transformApiDataToDeals = (apiData: ApiRiskFactorDeal[]): PivotalDeal[] => {
  return apiData.map((item) => ({
    id: item.deal_id,
    company: item.deal_info.deal_name,
    amount: item.deal_info.opportunity?.amount,
    salesRep: undefined, // Not provided in API response
    stage: item.deal_info.deal_stage,
    closeDate: item.deal_info.opportunity?.close_date,
    probability: undefined, // Not provided in API response
    riskScore: Math.abs(item.score), // Convert to positive number for display
    dealType: undefined, // Not provided in API response
    riskFactors: item.top_risks.map((risk, index) => ({
      theme: risk.theme_title || risk.category, // Use theme_title if available, fallback to category
      severity: risk.risk_priority as "high" | "medium" | "low",
      title: risk.title,
      description: risk.reason,
      score: risk.score || (100 - index * 10), // Use API score if available, otherwise use higher scores for earlier items
    })),
    nextSteps: item.call_summary?.action_items?.map((actionItem) => `${actionItem.assignee}: ${actionItem.item}`) || [],
    callSummary: item.call_summary?.call_summary,
    callSummaryCreatedAt: item.call_summary?.created_at,
  }))
}

export function PivotalDeals() {
  const [expandedDealIds, setExpandedDealIds] = useState<Set<string>>(new Set())
  const [visibleDealsCount, setVisibleDealsCount] = useState(8)
  const [deals, setDeals] = useState<PivotalDeal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [checkedNextSteps, setCheckedNextSteps] = useState<Record<string, Record<number, boolean>>>({})
  const initialVisibleDeals = 8

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true)
        const response = await getRiskFactorDeals()
        if (response) {
          const transformedDeals = transformApiDataToDeals(response)
          setDeals(transformedDeals)
        }
      } catch (err) {
        setError("Failed to load pivotal deals")
        console.error("Error fetching risk factor deals:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchDeals()
  }, [])

  const toggleDealExpansion = (dealId: string) => {
    setExpandedDealIds((prevExpandedDeals) => {
      const newExpandedDeals = new Set(prevExpandedDeals)
      if (newExpandedDeals.has(dealId)) {
        newExpandedDeals.delete(dealId)
      } else {
        newExpandedDeals.add(dealId)
      }
      return newExpandedDeals
    })
  }

  const handleNextStepCheckboxChange = (dealId: string, stepIndex: number) => {
    setCheckedNextSteps((prev) => ({
      ...prev,
      [dealId]: {
        ...prev[dealId],
        [stepIndex]: !prev[dealId]?.[stepIndex]
      }
    }))
  }

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Pivotal Deals</CardTitle>
          <CardDescription>High-value opportunities requiring attention due to risk factors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-500">Loading pivotal deals...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Pivotal Deals</CardTitle>
          <CardDescription>High-value opportunities requiring attention due to risk factors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Pivotal Deals</CardTitle>
        <CardDescription>High-value opportunities requiring attention due to risk factors</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {deals
            .sort((a: PivotalDeal, b: PivotalDeal) => {
              // First, prioritize "At Risk" deals (those without positive factors)
              const aHasPositive = a.riskFactors.some((factor: RiskFactor) => factor.severity === "positive")
              const bHasPositive = b.riskFactors.some((factor: RiskFactor) => factor.severity === "positive")

              if (!aHasPositive && bHasPositive) return -1 // a is at risk, b is not - a comes first
              if (aHasPositive && !bHasPositive) return 1 // b is at risk, a is not - b comes first

              // If both have same risk status, sort by close date (ascending - nearest first)
              if (a.closeDate && b.closeDate) {
                const dateA = new Date(a.closeDate)
                const dateB = new Date(b.closeDate)
                return dateA.getTime() - dateB.getTime()
              }
              return 0
            })
            .slice(0, visibleDealsCount)
            .map((deal: PivotalDeal) => (
              <Card
                key={deal.id}
                className={`border-amber-500 ${expandedDealIds.has(deal.id) ? "ring-1 ring-amber-500" : ""}`}
              >
                <CardContent className="p-3">
                  {/* Compact Deal Row */}
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleDealExpansion(deal.id)}
                  >
                    <div className="flex flex-col gap-1 flex-grow">
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/app/deals/${deal.id}`}
                          className="font-medium text-sm text-amber-800 hover:text-amber-900 hover:underline"
                        >
                          {deal.company}
                        </Link>
                        {deal.dealType && (
                          <Badge variant="outline" className="bg-gray-100 text-gray-800 hover:bg-gray-100 text-xs">
                            {deal.dealType}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center text-xs text-gray-600 gap-4">
                        {deal.probability && <span>{deal.probability}%</span>}
                        {deal.salesRep && (
                          <span className="flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            {deal.salesRep}
                          </span>
                        )}
                        {deal.amount && <span className="font-medium">${deal.amount.toLocaleString()}</span>}
                        {deal.closeDate && (
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(deal.closeDate).toLocaleDateString()}
                          </span>
                        )}
                          {deal.stage}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-4">
                      {(() => {
                        const filteredFactors = deal.riskFactors.filter((factor: RiskFactor) => {
                          const hasPositiveFactors = deal.riskFactors.some((f: RiskFactor) => f.severity === "positive")
                          return hasPositiveFactors ? factor.severity === "positive" : factor.severity !== "positive"
                        })
                        
                        // Get unique categories and their highest severity
                        const uniqueCategories = filteredFactors.reduce((acc: Record<string, RiskFactor>, factor: RiskFactor) => {
                          if (!acc[factor.theme]) {
                            acc[factor.theme] = factor
                          } else {
                            // Keep the highest severity for each category, or highest score if same severity
                            const severityOrder = { high: 3, medium: 2, low: 1, positive: 0 }
                            if (severityOrder[factor.severity] > severityOrder[acc[factor.theme].severity]) {
                              acc[factor.theme] = factor
                            } else if (severityOrder[factor.severity] === severityOrder[acc[factor.theme].severity]) {
                              // Same severity, check score
                              if ((factor.score || 0) > (acc[factor.theme].score || 0)) {
                                acc[factor.theme] = factor
                              }
                            }
                          }
                          return acc
                        }, {})
                        
                        // Sort the unique categories by priority and score, then filter out "Buyer Behavior" and limit to top 4
                        const sortedUniqueFactors = sortRiskFactors(Object.values(uniqueCategories))
                          .filter((factor: RiskFactor) => factor.theme !== "Buyer Behavior")
                          .slice(0, 4) // Limit to maximum 4 themes
                        
                        return sortedUniqueFactors.map((factor: RiskFactor, index: number) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="text-xs"
                          >
                            {factor.theme}
                          </Badge>
                        ))
                      })()}
                      {!deal.riskFactors.some((factor: RiskFactor) => factor.severity === "positive") && (() => {
                        // Determine the highest severity risk factor
                        const severityOrder = { high: 3, medium: 2, low: 1, positive: 0 }
                        const highestSeverityFactor = deal.riskFactors
                          .filter((factor: RiskFactor) => factor.severity !== "positive")
                          .reduce((highest: RiskFactor | null, current: RiskFactor) => {
                            if (!highest || severityOrder[current.severity] > severityOrder[highest.severity]) {
                              return current
                            }
                            return highest
                          }, null)
                        
                        const getBadgeClassName = (severity: string) => {
                          switch (severity) {
                            case "high":
                              return "bg-red-50 text-red-700 border-red-200 hover:bg-red-50"
                            case "medium":
                              return "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50"
                            case "low":
                              return "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50"
                            default:
                              return "bg-amber-100 text-amber-800 hover:bg-amber-100"
                          }
                        }
                        
                        return (
                          <Badge variant="outline" className={getBadgeClassName(highestSeverityFactor?.severity || "low")}>
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            At Risk
                          </Badge>
                        )
                      })()}
                      {expandedDealIds.has(deal.id) ? (
                        <ChevronUp className="h-4 w-4 text-gray-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedDealIds.has(deal.id) && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium mb-2">Risk Factors</h4>
                          <div className="space-y-2">
                            {sortRiskFactors(
                              deal.riskFactors.filter((factor: RiskFactor) => {
                                const hasPositiveFactors = deal.riskFactors.some((f: RiskFactor) => f.severity === "positive")
                                return hasPositiveFactors
                                  ? factor.severity === "positive"
                                  : factor.severity !== "positive"
                              })
                            ).map((factor: RiskFactor, index: number) => (
                                <div key={index} className="bg-white p-3 rounded-md border border-gray-200 shadow-sm">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge
                                      variant="outline"
                                    >
                                      {factor.theme}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={
                                        factor.severity === "high"
                                          ? "bg-red-50 text-red-700 border-red-200 text-xs"
                                          : factor.severity === "medium"
                                            ? "bg-orange-50 text-orange-700 border-orange-200 text-xs"
                                            : factor.severity === "positive"
                                              ? "bg-green-50 text-green-700 border-green-200 text-xs"
                                              : "bg-yellow-50 text-yellow-700 border-yellow-200 text-xs"
                                      }
                                    >
                                      {factor.severity === "high"
                                        ? "High Risk"
                                        : factor.severity === "medium"
                                          ? "Medium Risk"
                                          : factor.severity === "positive"
                                            ? "Positive Update"
                                            : "Low Risk"}
                                    </Badge>
                                  </div>
                                  <h5 className="text-xs font-semibold text-gray-800 mb-1">{factor.title}</h5>
                                  <p className="text-xs text-gray-600">{factor.description}</p>
                                </div>
                              ))}
                          </div>
                        </div>

                        <div>
                          {deal.callSummary && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium mb-2">
                                Last Call Summary
                                {deal.callSummaryCreatedAt && (
                                  <span className="text-xs font-normal text-gray-400"> - {moment(deal.callSummaryCreatedAt).format("MMM DD, YYYY")}</span>
                                )}
                              </h4>
                              <p className="text-xs text-gray-600 leading-relaxed">{deal.callSummary}</p>
                            </div>
                          )}

                          <h4 className="text-sm font-medium mb-2">Next Steps</h4>
                          {deal.nextSteps && deal.nextSteps.length > 0 ? (
                            <div>
                              {deal.nextSteps.map((step: string, index: number) => {
                                const checkboxId = `${deal.id}-step-${index}`;
                                const isChecked = checkedNextSteps[deal.id]?.[index] || false;
                                
                                return (
                                  <NextStepContainer key={index}>
                                    <LightCheckboxInput
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleNextStepCheckboxChange(deal.id, index)}
                                      id={checkboxId}
                                    />
                                    <LightCheckboxLabel
                                      className="box"
                                      htmlFor={checkboxId}
                                    >
                                      <CheckWithCircle />
                                    </LightCheckboxLabel>
                                    <NextStepText>
                                      {isChecked ? (
                                        <StrikeThroughText className="text-xs">
                                          {step}
                                        </StrikeThroughText>
                                      ) : (
                                        <p className="text-xs">{step}</p>
                                      )}
                                    </NextStepText>
                                  </NextStepContainer>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">No action items available</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

          {deals.length > visibleDealsCount && (
            <div className="flex justify-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVisibleDealsCount(Math.min(deals.length, visibleDealsCount + 5))}
                className="text-blue-600 hover:text-blue-800"
              >
                See More
              </Button>
            </div>
          )}

          {visibleDealsCount > initialVisibleDeals && deals.length > initialVisibleDeals && (
            <div className="flex justify-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVisibleDealsCount(initialVisibleDeals)}
                className="text-blue-600 hover:text-blue-800"
              >
                Show Less
              </Button>
            </div>
          )}

          {deals.length === 0 && !loading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">No pivotal deals found</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
